/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {MaterialAddIcon} from '../../../icons/current/MaterialAddIcon';
import Render from '../../../render/Render';
import {dlsColor} from '../../../theme/theme';
import {IconSize} from '../../../types/types';
import {PulseLoader} from '../../loading/PulseLoader';

export interface VisionShowMoreCtaProps {
  onClick: MouseEventHandler<HTMLDivElement>;
  label: string;
  // the next events are loading - the row shows a pulse loader in place of
  // its button ...
  isLoading?: boolean;
}

const useStyles = makeStyles(() => ({
  row : {
    boxSizing       : 'border-box',
    display         : 'flex',
    justifyContent  : 'center',
    alignItems      : 'center',
    height          : '44px',
    padding         : '0px 16px',
    gap             : '8px',
    backgroundColor : dlsColor('white')
  },
  cta : {
    boxSizing    : 'border-box',
    display      : 'flex',
    alignItems   : 'center',
    padding      : '0px 8px',
    gap          : '4px',
    borderRadius : '4px',
    cursor       : 'pointer'
  },
  label : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    color         : dlsColor('darkestBlue')
  }
}));

/**
 * VisionShowMoreCta renders the current theme's "+ Show More" row that sits
 * beneath the Vision history list, letting a caller page in additional
 * camera events. It is a plain presentational button - the actual
 * pagination (e.g. `fetchNextPage`/mobile reveal) stays owned by whatever
 * container wires `onClick`, unchanged from today's behavior. While the next
 * events load (`isLoading`), a pulse loader takes the button's place.
 *
 * ```typescript
 *
 *   import {VisionShowMoreCta} from
 *     '@ion/dls/src/components/vision/current/VisionShowMoreCta';
 *   ...
 *
 *   <VisionShowMoreCta onClick={fetchNextPage}
 *     label='Show More'
 *     isLoading={isFetchingNextPage} />
 *
 * ```
 *
 */
export const VisionShowMoreCta = ({
  onClick,
  label,
  isLoading = false
}: VisionShowMoreCtaProps) => {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <Render.If condition={isLoading}>
        <PulseLoader />
      </Render.If>
      <Render.If condition={!isLoading}>
        <div className={classes.cta} onClick={onClick}>
          <MaterialAddIcon size={IconSize.small} color={dlsColor('darkestBlue')} />
          <span className={classes.label}>
            {label}
          </span>
        </div>
      </Render.If>
    </div>
  );
};

export default React.memo(VisionShowMoreCta);
